import React, { useState, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { theme } from '@/utils/util_theme';
import { useSearchRoomContext } from '@/features/marketplace/hooks/use-search-room';
import { SearchBarItems } from '@/features/marketplace/components/search-bar-items';
import clsx from 'clsx';
import { useLocale } from '@/hooks/use-locale';
import { navigateToCity } from '@/features/marketplace/utils/city-navigation';
import { COLOC_MAIN_APP } from '@/config';
import { CitiesDropdownButton } from '@/features/marketplace/components/cities-dropdown-button';
import { MobileSearchBar } from './MobileSearchBar';
import { TakeMeEverywhereButton } from './TakeMeEverywhereButton';

const SearchBarHero = () => {
  const [inputActive, setInputActive] = useState(false);
  const { setSearchQuery, fetchSearchRoomResults, searchQuery, debouncedSearchQuery } =
    useSearchRoomContext()!;
  const { t } = useTranslation();
  const { isLoading, isFetching, data } = fetchSearchRoomResults;
  const { currentLocale } = useLocale();

  const isPending = !!searchQuery && searchQuery !== debouncedSearchQuery;
  const isWaiting = isPending || isFetching;
  const firstResult = data?.data?.[0];
  const showTakeMeEverywhereButton = !searchQuery || !isLoading;

  const handleCitySelect = useCallback(
    (city: any) => {
      navigateToCity(city, currentLocale);
    },
    [currentLocale]
  );

  const handleAllCitiesSelect = useCallback(() => {
    window.location.href = `${COLOC_MAIN_APP}/${currentLocale}/shared-houses`;
  }, [currentLocale]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery]
  );

  const handleFocus = useCallback(() => {
    setInputActive(true);
  }, []);

  const onBlur = () => {
    setTimeout(() => {
      setInputActive(false);
    }, 100);
  };

  const handleSearch = () => {
    if (isWaiting) return;
    if (searchQuery && firstResult) {
      window.location.href = firstResult.path;
    } else {
      window.location.href = `${COLOC_MAIN_APP}/${currentLocale}/shared-houses`;
    }
  };

  return (
    <div className="relative w-full">
      {/* Desktop search bar — visible at lg (1024px) and above */}
      <div className="hidden lg:block">
        <div
          className={clsx(
            'flex h-[100px] w-full items-center rounded-[24px] px-4',
            inputActive ? 'bg-white' : 'bg-white/30',
            'backdrop-blur-[10px]'
          )}
        >
          <input
            className={clsx(
              'hero-search flex-grow bg-transparent',
              'text-lead1 font-medium text-N900',
              'focus:outline-none',
              inputActive ? 'placeholder-gray-500' : 'placeholder-white'
            )}
            type="text"
            placeholder={t('js.components.search_bar_hero.placeholder') ?? ''}
            onChange={handleSearchChange}
            onFocus={handleFocus}
            onBlur={onBlur}
          />

          <style>
            {`.placeholder-white::placeholder {
          color: white;
        }
        .placeholder-gray-500::placeholder {
          color: gray;
        }`}
          </style>

          <div className="flex flex-row gap-4">
            <CitiesDropdownButton
              selectedCity={null}
              onCitySelect={handleCitySelect}
              onAllCitiesSelect={handleAllCitiesSelect}
              inputActive={inputActive}
            />
            <button
              type="button"
              aria-busy={isWaiting}
              disabled={isWaiting}
              className={clsx(
                'flex h-[56px] items-center justify-center rounded-[16px]',
                'px-4 py-3',
                'text-[16px] font-semibold text-text-primary',
                'bg-R300',
                'hover:bg-R200 hover:shadow-[0_0_20px] hover:shadow-R200/50',
                isWaiting && 'cursor-wait opacity-70'
              )}
              onClick={handleSearch}
            >
              <IconSaxIcon
                name="search-normal-1"
                size={24}
                strokeColor={theme?.colors.black || '#000000'}
                strokeWidth={2}
                className="mr-2"
              />
              {t('js.components.search_bar_hero.search_button')}
            </button>
          </div>
        </div>
        <Transition
          show={inputActive}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
          className="relative z-20"
        >
          {/* Arrow */}
          <div className="absolute left-[50px] top-[1.1rem] z-40 -translate-x-1/2">
            <div className="h-0 w-0 border-x-[18px] border-b-[18px] border-x-transparent border-b-white" />
          </div>
          {/* Dropdown */}
          <div className="scrollbar-hide absolute top-[1.6rem] z-30 mt-1 flex max-h-[28rem] w-[400px] flex-col overflow-y-auto rounded-[32px] bg-white shadow-medium">
            <div className="flex flex-col p-4">
              <SearchBarItems style="hero" />
            </div>
            {showTakeMeEverywhereButton && (
              <div className="sticky bottom-0 left-0 w-full bg-white p-4 pt-2">
                <TakeMeEverywhereButton className="bg-[#F3F4F5] px-3 py-2" iconSize={24} />
              </div>
            )}
          </div>
        </Transition>
      </div>

      {/* Mobile search bar — visible below lg (1024px) */}
      <div className="lg:hidden">
        <MobileSearchBar />
      </div>
    </div>
  );
};

export { SearchBarHero };
